import {NgxNotification, NgxNotificationOptions, NgxNotificationType} from '@ngx-notifications/notifications.interface';
import {NgxTranslation} from '@ngx-translations/translations.interfaces';
import {Injectable, Optional} from '@angular/core';
import {BehaviorSubject} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class NgxNotifications {

    // The default titles for the notification types.
    private readonly _defaultTitles = {
        [NgxNotificationType.Success]: 'Action was successful',
        [NgxNotificationType.Error]: 'Action did fail',
        [NgxNotificationType.Warning]: 'Warning',
        [NgxNotificationType.Info]: 'Information'
    };

    // The default icons for the notification types.
    private readonly _defaultIcons = {
        [NgxNotificationType.Success]: 'check_circle',
        [NgxNotificationType.Error]: 'error_outline',
        [NgxNotificationType.Warning]: 'error_outline',
        [NgxNotificationType.Info]: 'info_outline'
    };

    // The default timeout and if the notification is closable.
    private readonly _defaultTimeout: number = 4000;
    private readonly _defaultClosable: boolean = true;

    // The data containing the notifications.
    private _list$ = new BehaviorSubject<NgxNotification[]>([]);

    constructor(@Optional() private _options: NgxNotificationOptions) {
        if (!_options) {
            throw new Error('No NotificationOptions were received.');
        }
        this._defaultTimeout = _options.timeout === undefined ? this._defaultTimeout : _options.timeout;
        this._defaultClosable = _options.closable === undefined ? this._defaultClosable : _options.closable;
        this._defaultTitles = Object.assign(this._defaultTitles, _options.titles || {});
        this._defaultIcons = Object.assign(this._defaultIcons, _options.icons || {});
    }

    get items() {
        return this._list$;
    }

    success(text: string | NgxTranslation, timeout = this._defaultTimeout) {
        return this.create({text, timeout, type: NgxNotificationType.Success});
    }

    error(text: string | NgxTranslation, timeout = this._defaultTimeout) {
        return this.create({text, timeout, type: NgxNotificationType.Error});
    }

    warn(text: string | NgxTranslation, timeout = this._defaultTimeout) {
        return this.create({text, timeout, type: NgxNotificationType.Warning});
    }

    info(text: string | NgxTranslation, timeout = this._defaultTimeout) {
        return this.create({text, timeout, type: NgxNotificationType.Info});
    }

    create(notification: NgxNotification) {
        // Compose a new notification item.
        const item = this.compose(notification);

        // Add the destroy function to the notification.
        item.destroy = () => this.pull(item);

        // Add the notification to the list.
        this.push(item);

        // Return the notification.
        return item;
    }

    private push(notification: NgxNotification) {
        // Get the current list.
        const list = this.items.getValue();

        // Add the notification to the list.
        list.push(notification);

        // Push a new list.
        this.items.next(list);
    }

    private pull(notification: NgxNotification) {
        // Get the current list.
        const list = this.items.getValue();

        // Remove the notifications with matching id's.
        list.forEach((item, index) => {
            if (item.id === notification.id) {
                list.splice(index, 1);
            }
        });

        // Push a new list.
        this.items.next(list);
    }

    private compose(notification: NgxNotification) {
        // Attach a random id to the notification.
        notification.id = Math.random().toString(36).substr(2, 10);

        // Set the default to true if none is set.
        notification.closable = notification.closable !== undefined ? notification.closable : this._defaultClosable;

        // Set a default title if none is set.
        notification.title = notification.title || this._defaultTitles[notification.type];

        // Set a default icon if none is set.
        notification.icon = notification.icon || this._defaultIcons[notification.type];

        // Set a default timeout if none is set.
        notification.timeout = notification.timeout !== undefined ? notification.timeout : this._defaultTimeout;

        // Return the composed notification.
        return notification;
    }

}
